/**
 * Column Selector Component
 *
 * @module shared/components/data-display/ColumnSelector
 *
 * Dialog component that allows users to select which columns to display in a table.
 * Shows checkboxes for all available columns and respects permission flags.
 *
 * @example
 * ```tsx
 * <ColumnSelector
 *   columns={availableColumns}
 *   visibleColumns={visibleColumns}
 *   onColumnsChange={handleColumnsChange}
 *   open={open}
 *   onClose={handleClose}
 * />
 * ```
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ============================================================================
// Types
// ============================================================================

export interface ColumnOption {
  /**
   * Unique key for the column
   */
  key: string;
  /**
   * Display label for the column
   */
  label: string;
  /**
   * Whether the column can be shown (permission check)
   */
  canShow: boolean;
  /**
   * Whether the column is visible by default
   */
  isVisibleDefault: boolean;
}

export interface ColumnSelectorProps {
  /**
   * All available columns
   */
  columns: ColumnOption[];
  /**
   * Set of currently visible column keys
   */
  visibleColumns: Set<string>;
  /**
   * Callback when column visibility changes
   * @param visibleColumns - New set of visible column keys
   */
  onColumnsChange: (visibleColumns: Set<string>) => void;
  /**
   * Current column order
   */
  columnOrder: string[];
  /**
   * Callback when column order changes
   * @param columnOrder - New array of column keys in order
   */
  onColumnOrderChange: (columnOrder: string[]) => void;
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when dialog should close
   */
  onClose: () => void;
  /**
   * Dialog title
   * @default 'Select Columns'
   */
  title?: string;
}

interface SortableColumnItemProps {
  column: ColumnOption;
  isChecked: boolean;
  isDefault: boolean;
  onToggle: () => void;
}

function SortableColumnItem({ column, isChecked, isDefault, onToggle }: SortableColumnItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox checked={isChecked} onChange={onToggle} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{column.label}</Typography>
          {isDefault && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                fontStyle: 'italic',
              }}
            >
              (default)
            </Typography>
          )}
        </Box>
      </Box>
      <IconButton
        aria-label={`Reorder ${column.label}`}
        size="small"
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ============================================================================
// Component
// ============================================================================

export function ColumnSelector({
  columns,
  visibleColumns,
  onColumnsChange,
  columnOrder,
  onColumnOrderChange,
  open,
  onClose,
  title = 'Select Columns',
}: ColumnSelectorProps) {
  // Local state for checkboxes (allows cancel to revert changes)
  const [localVisibleColumns, setLocalVisibleColumns] = useState<Set<string>>(visibleColumns);
  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>(columnOrder);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  // Update local state when visibleColumns prop changes
  useEffect(() => {
    setLocalVisibleColumns(new Set(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    setLocalColumnOrder(columnOrder);
  }, [columnOrder]);

  // Handle checkbox change
  const handleToggle = (columnKey: string) => {
    setLocalVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnKey)) {
        next.delete(columnKey);
      } else {
        next.add(columnKey);
      }
      return next;
    });
  };

  // Handle apply
  const handleApply = () => {
    onColumnsChange(new Set(localVisibleColumns));
    onColumnOrderChange(normalizeOrder(localColumnOrder));
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    // Revert to original state
    setLocalVisibleColumns(new Set(visibleColumns));
    setLocalColumnOrder([...columnOrder]);
    onClose();
  };

  // Handle select all
  const handleSelectAll = () => {
    const allVisible = new Set(
      columns.filter((col) => col.canShow).map((col) => col.key)
    );
    setLocalVisibleColumns(allVisible);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setLocalVisibleColumns(new Set());
  };

  // Filter columns by permission
  const availableColumns = columns.filter((col) => col.canShow);
  const availableColumnMap = useMemo(
    () => new Map(availableColumns.map((col) => [col.key, col])),
    [availableColumns]
  );

  const normalizeOrder = useCallback(
    (order: string[]) => {
      const filtered = order.filter((key) => availableColumnMap.has(key));
      const missing = availableColumns.map((col) => col.key).filter((key) => !filtered.includes(key));
      return [...filtered, ...missing];
    },
    [availableColumnMap, availableColumns]
  );

  const orderedAvailableColumns = useMemo(() => {
    const baseOrder = localColumnOrder.length > 0 ? localColumnOrder : availableColumns.map((col) => col.key);
    const normalized = normalizeOrder(baseOrder);
    return normalized
      .map((key) => availableColumnMap.get(key))
      .filter((col): col is ColumnOption => !!col);
  }, [localColumnOrder, availableColumns, availableColumnMap, normalizeOrder]);

  // Check if all available columns are selected
  const allSelected = availableColumns.length > 0 && 
    availableColumns.every((col) => localVisibleColumns.has(col.key));

  // Check if none are selected
  const noneSelected = localVisibleColumns.size === 0;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setLocalColumnOrder((prev) => {
      const normalized = normalizeOrder(prev);
      const oldIndex = normalized.indexOf(String(active.id));
      const newIndex = normalized.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return normalized;
      }
      return arrayMove(normalized, oldIndex, newIndex);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: 400,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={allSelected}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={handleDeselectAll}
            disabled={noneSelected}
          >
            Deselect All
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {localVisibleColumns.size} of {availableColumns.length} selected
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Column checkboxes */}
        {availableColumns.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No columns available
            </Typography>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={orderedAvailableColumns.map((column) => column.key)} strategy={verticalListSortingStrategy}>
                <Box>
                  {orderedAvailableColumns.map((column) => (
                    <SortableColumnItem
                      key={column.key}
                      column={column}
                      isChecked={localVisibleColumns.has(column.key)}
                      isDefault={column.isVisibleDefault}
                      onToggle={() => handleToggle(column.key)}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
